/**
 * API 客户端：fetch 封装 + JWT（内存 accessToken + httpOnly cookie lc_refresh）
 * - 401 时自动 refresh 重试一次
 * - 请求体严格对齐 DTO 白名单（forbidNonWhitelisted），不携带任何多余字段
 */
import type {
  ApiErrorBody,
  Area,
  AuditLogRow,
  AuthResponse,
  ChangePasswordBody,
  Commitment,
  Contact,
  ContactDashboard,
  ContactDetail,
  ContactGroup,
  ContactGroupWithCount,
  ContactLink,
  CreateAreaBody,
  CreateCommitmentBody,
  CreateContactBody,
  CreateFocusBody,
  CreateGiftBody,
  CreateGroupBody,
  CreateHabitBody,
  CreateHabitLogBody,
  CreateImportantDateBody,
  CreateInteractionBody,
  CreateLinkBody,
  CreateProjectBody,
  CreateRelationBody,
  CreateTaskBody,
  EndFocusBody,
  FocusSession,
  Gift,
  Habit,
  HabitLog,
  ImportantDate,
  Interaction,
  LoginBody,
  NotificationRow,
  Page,
  Project,
  RegisterBody,
  SessionUser,
  StatsResult,
  TagWithCount,
  Task,
  TasksQueryParams,
  UpdateAreaBody,
  UpdateCommitmentBody,
  UpdateContactBody,
  UpdateHabitBody,
  UpdateInteractionBody,
  UpdateProjectBody,
  UpdateTaskBody,
  VaultChangePasswordBody,
  VaultFolder,
  VaultInitBody,
  VaultItem,
  VaultItemCreateBody,
  VaultItemHistoryRow,
  VaultItemUpdateBody,
  VaultRecoverBody,
  VaultStatus,
  VaultUnlockBody,
  VaultUnlockResult,
} from './types';

const BASE = '/api';

// ==================== 错误 ====================

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, body: ApiErrorBody) {
    super(Array.isArray(body.message) ? body.message.join('；') : body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }
}

// ==================== Token 管理（模块级，避免 store 循环依赖） ====================

let accessToken: string | null = null;
let authListener: ((res: AuthResponse | null) => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** 注册会话变化监听（登录/刷新/登出时通知 auth store 更新 user） */
export function setAuthListener(fn: ((res: AuthResponse | null) => void) | null): void {
  authListener = fn;
}

// ==================== 核心请求 ====================

function buildQuery(params?: Record<string, string | number | boolean | undefined | null>): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** 跳过 401 自动刷新（refresh 本身） */
  noRefresh?: boolean;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: ApiErrorBody = { code: 'UNKNOWN', message: `请求失败（${res.status}）` };
  try {
    const data = (await res.json()) as Partial<ApiErrorBody> | null;
    if (data && typeof data === 'object') {
      body = {
        code: String(data.code ?? 'UNKNOWN'),
        message: data.message ?? body.message,
        details: data.details,
      };
    }
  } catch {
    /* 非 JSON 响应，保留默认信息 */
  }
  return new ApiError(res.status, body);
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, noRefresh } = opts;
  const doFetch = () =>
    fetch(`${BASE}${path}${buildQuery(query)}`, {
      method,
      credentials: 'include',
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

  let res = await doFetch();
  if (res.status === 401 && !noRefresh && !path.startsWith('/auth/refresh')) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await doFetch();
    }
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** 401 自动续签；成功返回 true 并更新 token/user */
async function tryRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) {
      accessToken = null;
      authListener?.(null);
      return false;
    }
    const data = (await res.json()) as AuthResponse;
    accessToken = data.accessToken;
    authListener?.(data);
    return true;
  } catch {
    accessToken = null;
    authListener?.(null);
    return false;
  }
}

// ==================== 认证 ====================

export async function register(body: RegisterBody): Promise<AuthResponse> {
  const data = await request<AuthResponse>('/auth/register', { method: 'POST', body });
  accessToken = data.accessToken;
  authListener?.(data);
  return data;
}

export async function login(body: LoginBody): Promise<AuthResponse> {
  const data = await request<AuthResponse>('/auth/login', { method: 'POST', body });
  accessToken = data.accessToken;
  authListener?.(data);
  return data;
}

export async function logout(): Promise<void> {
  try {
    await request<{ ok: true }>('/auth/logout', { method: 'POST' });
  } finally {
    accessToken = null;
    authListener?.(null);
  }
}

export async function fetchMe(): Promise<SessionUser> {
  // GET /auth/me 直接返回 SessionUser（非 {user} 包装）
  return request<SessionUser>('/auth/me');
}

export async function changeLoginPassword(body: ChangePasswordBody): Promise<{ ok: boolean; accessToken: string; accessExpiresIn: number }> {
  const data = await request<{ ok: boolean; accessToken: string; accessExpiresIn: number }>('/auth/password', {
    method: 'POST',
    body,
  });
  accessToken = data.accessToken;
  return data;
}

// ==================== 任务域 · 领域 ====================

export function listAreas(query?: { archived?: string }): Promise<Area[]> {
  return request<Area[]>('/tasks/areas', { query });
}

export function createArea(body: CreateAreaBody): Promise<Area> {
  return request<Area>('/tasks/areas', { method: 'POST', body });
}

export function updateArea(id: string, body: UpdateAreaBody): Promise<Area> {
  return request<Area>(`/tasks/areas/${id}`, { method: 'PATCH', body });
}

export function deleteArea(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/tasks/areas/${id}`, { method: 'DELETE' });
}

// ==================== 任务域 · 项目 ====================

export function listProjects(query?: {
  status?: string;
  areaId?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}): Promise<Page<Project>> {
  return request<Page<Project>>('/tasks/projects', { query });
}

export function getProject(id: string): Promise<Project> {
  return request<Project>(`/tasks/projects/${id}`);
}

export function createProject(body: CreateProjectBody): Promise<Project> {
  return request<Project>('/tasks/projects', { method: 'POST', body });
}

export function updateProject(id: string, body: UpdateProjectBody): Promise<Project> {
  return request<Project>(`/tasks/projects/${id}`, { method: 'PATCH', body });
}

export function completeProject(id: string): Promise<Project> {
  return request<Project>(`/tasks/projects/${id}/complete`, { method: 'POST' });
}

export function deleteProject(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/tasks/projects/${id}`, { method: 'DELETE' });
}

// ==================== 任务域 · 标签 ====================

export function listTags(): Promise<TagWithCount[]> {
  return request<TagWithCount[]>('/tasks/tags');
}

// ==================== 任务域 · 任务 ====================

export function listTasks(query?: TasksQueryParams): Promise<Page<Task>> {
  return request<Page<Task>>('/tasks', { query });
}

export function getTask(id: string): Promise<Task> {
  return request<Task>(`/tasks/${id}`);
}

export function createTask(body: CreateTaskBody): Promise<Task> {
  return request<Task>('/tasks', { method: 'POST', body });
}

export function updateTask(id: string, body: UpdateTaskBody): Promise<Task> {
  return request<Task>(`/tasks/${id}`, { method: 'PATCH', body });
}

export function deleteTask(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/tasks/${id}`, { method: 'DELETE' });
}

export function completeTask(id: string): Promise<Task> {
  return request<Task>(`/tasks/${id}/complete`, { method: 'POST' });
}

export function cancelTask(id: string): Promise<Task> {
  return request<Task>(`/tasks/${id}/cancel`, { method: 'POST' });
}

export function addSubtask(taskId: string, title: string): Promise<Task> {
  return request<Task>(`/tasks/${taskId}/subtasks`, { method: 'POST', body: { title } });
}

export function updateSubtask(taskId: string, sid: string, body: { title?: string; done?: boolean }): Promise<Task> {
  return request<Task>(`/tasks/${taskId}/subtasks/${sid}`, { method: 'PATCH', body });
}

export function deleteSubtask(taskId: string, sid: string): Promise<Task> {
  return request<Task>(`/tasks/${taskId}/subtasks/${sid}`, { method: 'DELETE' });
}

export function attachTag(taskId: string, name: string): Promise<{ id: string; name: string; color: string | null; count: number }> {
  return request(`/tasks/${taskId}/tags`, { method: 'POST', body: { name } });
}

export function detachTag(taskId: string, name: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/tasks/${taskId}/tags/${encodeURIComponent(name)}`, { method: 'DELETE' });
}

export function addReminder(taskId: string, body: { remindAt: string; channel: 'INAPP' | 'WEBHOOK' | 'EMAIL' }): Promise<Task> {
  return request<Task>(`/tasks/${taskId}/reminders`, { method: 'POST', body });
}

export function deleteReminder(taskId: string, rid: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/tasks/${taskId}/reminders/${rid}`, { method: 'DELETE' });
}

// ==================== 任务域 · 习惯 ====================

export function listHabits(query?: { archived?: string }): Promise<Habit[]> {
  return request<Habit[]>('/tasks/habits', { query });
}

export function createHabit(body: CreateHabitBody): Promise<Habit> {
  return request<Habit>('/tasks/habits', { method: 'POST', body });
}

export function updateHabit(id: string, body: UpdateHabitBody): Promise<Habit> {
  return request<Habit>(`/tasks/habits/${id}`, { method: 'PATCH', body });
}

export function deleteHabit(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/tasks/habits/${id}`, { method: 'DELETE' });
}

export function upsertHabitLog(habitId: string, body: CreateHabitLogBody): Promise<HabitLog> {
  return request<HabitLog>(`/tasks/habits/${habitId}/logs`, { method: 'POST', body });
}

export function listHabitLogs(habitId: string, query?: { from?: string; to?: string }): Promise<HabitLog[]> {
  return request<HabitLog[]>(`/tasks/habits/${habitId}/logs`, { query });
}

// ==================== 任务域 · 专注 ====================

export function listFocus(query?: {
  from?: string;
  to?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}): Promise<Page<FocusSession>> {
  return request<Page<FocusSession>>('/tasks/focus', { query });
}

export function startFocus(body: CreateFocusBody): Promise<FocusSession> {
  return request<FocusSession>('/tasks/focus', { method: 'POST', body });
}

export function endFocus(id: string, body: EndFocusBody): Promise<FocusSession> {
  return request<FocusSession>(`/tasks/focus/${id}/end`, { method: 'PATCH', body });
}

// ==================== 任务域 · 统计 ====================

export function getStats(query?: { from?: string; to?: string }): Promise<StatsResult> {
  return request<StatsResult>('/tasks/stats', { query });
}

// ==================== 人脉域 ====================

export function getContactsDashboard(): Promise<ContactDashboard> {
  return request<ContactDashboard>('/contacts/dashboard');
}

export function listContacts(query?: {
  groupId?: string;
  favorite?: string;
  needsContact?: string;
  archived?: string;
  sort?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}): Promise<Page<Contact>> {
  return request<Page<Contact>>('/contacts', { query });
}

export function getContact(id: string): Promise<ContactDetail> {
  return request<ContactDetail>(`/contacts/${id}`);
}

export function createContact(body: CreateContactBody): Promise<Contact> {
  return request<Contact>('/contacts', { method: 'POST', body });
}

export function updateContact(id: string, body: UpdateContactBody): Promise<Contact> {
  return request<Contact>(`/contacts/${id}`, { method: 'PATCH', body });
}

export function deleteContact(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/contacts/${id}`, { method: 'DELETE' });
}

export function archiveContact(id: string): Promise<Contact> {
  return request<Contact>(`/contacts/${id}/archive`, { method: 'POST' });
}

export function listInteractions(
  contactId: string,
  query?: { page?: number; pageSize?: number; q?: string },
): Promise<Page<Interaction>> {
  return request<Page<Interaction>>(`/contacts/${contactId}/interactions`, { query });
}

export function createInteraction(contactId: string, body: CreateInteractionBody): Promise<Interaction> {
  return request<Interaction>(`/contacts/${contactId}/interactions`, { method: 'POST', body });
}

export function updateInteraction(id: string, body: UpdateInteractionBody): Promise<Interaction> {
  return request<Interaction>(`/contacts/interactions/${id}`, { method: 'PATCH', body });
}

export function deleteInteraction(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/contacts/interactions/${id}`, { method: 'DELETE' });
}

export function createLink(contactId: string, body: CreateLinkBody): Promise<ContactLink> {
  return request<ContactLink>(`/contacts/${contactId}/links`, { method: 'POST', body });
}

/** DELETE /contacts/links/:lid（不嵌套在联系人下） */
export function deleteLink(lid: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/contacts/links/${lid}`, { method: 'DELETE' });
}

export function createRelation(contactId: string, body: CreateRelationBody): Promise<ContactDetail> {
  return request<ContactDetail>(`/contacts/${contactId}/relations`, { method: 'POST', body });
}

export function deleteRelation(contactId: string, rid: string): Promise<ContactDetail> {
  return request<ContactDetail>(`/contacts/${contactId}/relations/${rid}`, { method: 'DELETE' });
}

export function listImportantDates(query?: {
  contactId?: string;
  upcoming?: number;
  page?: number;
  pageSize?: number;
  q?: string;
}): Promise<Page<ImportantDate>> {
  return request<Page<ImportantDate>>('/contacts/important-dates', { query });
}

export function createImportantDate(body: CreateImportantDateBody): Promise<ImportantDate> {
  return request<ImportantDate>('/contacts/important-dates', { method: 'POST', body });
}

export function updateImportantDate(id: string, body: Partial<CreateImportantDateBody>): Promise<ImportantDate> {
  return request<ImportantDate>(`/contacts/important-dates/${id}`, { method: 'PATCH', body });
}

export function deleteImportantDate(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/contacts/important-dates/${id}`, { method: 'DELETE' });
}

export function listCommitments(query?: {
  status?: string;
  contactId?: string;
  overdue?: string;
  page?: number;
  pageSize?: number;
  q?: string;
}): Promise<Page<Commitment>> {
  return request<Page<Commitment>>('/contacts/commitments', { query });
}

export function createCommitment(body: CreateCommitmentBody): Promise<Commitment> {
  return request<Commitment>('/contacts/commitments', { method: 'POST', body });
}

export function updateCommitment(id: string, body: UpdateCommitmentBody): Promise<Commitment> {
  return request<Commitment>(`/contacts/commitments/${id}`, { method: 'PATCH', body });
}

export function completeCommitment(id: string): Promise<Commitment> {
  return request<Commitment>(`/contacts/commitments/${id}/complete`, { method: 'POST' });
}

export function deleteCommitment(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/contacts/commitments/${id}`, { method: 'DELETE' });
}

export function listGifts(query?: {
  contactId?: string;
  direction?: string;
  page?: number;
  pageSize?: number;
  q?: string;
}): Promise<Page<Gift>> {
  return request<Page<Gift>>('/contacts/gifts', { query });
}

export function createGift(body: CreateGiftBody): Promise<Gift> {
  return request<Gift>('/contacts/gifts', { method: 'POST', body });
}

export function deleteGift(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/contacts/gifts/${id}`, { method: 'DELETE' });
}

export function listGroups(): Promise<ContactGroupWithCount[]> {
  return request<ContactGroupWithCount[]>('/contacts/groups');
}

export function createGroup(body: CreateGroupBody): Promise<ContactGroup> {
  return request<ContactGroup>('/contacts/groups', { method: 'POST', body });
}

export function updateGroup(id: string, body: Partial<CreateGroupBody>): Promise<ContactGroup> {
  return request<ContactGroup>(`/contacts/groups/${id}`, { method: 'PATCH', body });
}

export function deleteGroup(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/contacts/groups/${id}`, { method: 'DELETE' });
}

export function addGroupMembers(id: string, contactIds: string[]): Promise<ContactGroup> {
  return request<ContactGroup>(`/contacts/groups/${id}/members`, { method: 'POST', body: { contactIds } });
}

/** DELETE /contacts/groups/:id/members/:cid（单个移除） */
export function removeGroupMember(id: string, cid: string): Promise<ContactGroup> {
  return request<ContactGroup>(`/contacts/groups/${id}/members/${cid}`, { method: 'DELETE' });
}

// ==================== Vault ====================

export function vaultStatus(): Promise<VaultStatus> {
  return request<VaultStatus>('/vault/status');
}

export function vaultInit(body: VaultInitBody): Promise<{ initialized: true; hasRecovery: boolean }> {
  return request('/vault/init', { method: 'POST', body });
}

export function vaultUnlock(body: VaultUnlockBody): Promise<VaultUnlockResult> {
  return request<VaultUnlockResult>('/vault/unlock', { method: 'POST', body });
}

export function vaultRecover(body: VaultRecoverBody): Promise<VaultUnlockResult> {
  return request<VaultUnlockResult>('/vault/recover', { method: 'POST', body });
}

export function vaultChangePassword(body: VaultChangePasswordBody): Promise<{ success: boolean; hasRecovery: boolean }> {
  return request('/vault/change-password', { method: 'POST', body });
}

export function listVaultFolders(): Promise<VaultFolder[]> {
  return request<VaultFolder[]>('/vault/folders');
}

export function createVaultFolder(body: { name: string; icon?: string; parentId?: string }): Promise<VaultFolder> {
  return request<VaultFolder>('/vault/folders', { method: 'POST', body });
}

export function updateVaultFolder(id: string, body: { name?: string; icon?: string | null; parentId?: string | null }): Promise<VaultFolder> {
  return request<VaultFolder>(`/vault/folders/${id}`, { method: 'PATCH', body });
}

export function deleteVaultFolder(id: string): Promise<{ success: true }> {
  return request<{ success: true }>(`/vault/folders/${id}`, { method: 'DELETE' });
}

export function listVaultItems(query?: {
  type?: string;
  folderId?: string;
  favorite?: string;
  archived?: string;
  sort?: string;
  page?: number;
  pageSize?: number;
}): Promise<Page<VaultItem>> {
  return request<Page<VaultItem>>('/vault/items', { query });
}

export function getVaultItem(id: string): Promise<VaultItem> {
  return request<VaultItem>(`/vault/items/${id}`);
}

export function createVaultItem(body: VaultItemCreateBody): Promise<VaultItem> {
  return request<VaultItem>('/vault/items', { method: 'POST', body });
}

export function updateVaultItem(id: string, body: VaultItemUpdateBody): Promise<VaultItem> {
  return request<VaultItem>(`/vault/items/${id}`, { method: 'PATCH', body });
}

export function deleteVaultItem(id: string): Promise<{ success: true }> {
  return request<{ success: true }>(`/vault/items/${id}`, { method: 'DELETE' });
}

export function markVaultItemUsed(id: string): Promise<VaultItem> {
  return request<VaultItem>(`/vault/items/${id}/usage`, { method: 'POST' });
}

export function listVaultItemHistory(id: string, query?: { page?: number; pageSize?: number }): Promise<Page<VaultItemHistoryRow>> {
  return request<Page<VaultItemHistoryRow>>(`/vault/items/${id}/history`, { query });
}

// ==================== 通知 / 审计 ====================

export function listNotifications(query?: { page?: number; pageSize?: number }): Promise<Page<NotificationRow>> {
  return request<Page<NotificationRow>>('/notifications', { query });
}

export function markNotificationRead(id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/notifications/${id}/read`, { method: 'PATCH' });
}

export function listAudit(query?: { page?: number; pageSize?: number }): Promise<Page<AuditLogRow>> {
  return request<Page<AuditLogRow>>('/audit', { query });
}
